import React, { useEffect, useMemo, useState } from "react";
import { Select, DatePicker, InputNumber, Button, Modal, message, Input, Popconfirm, Tooltip } from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  ClientApartment,
  ApartmentExtra,
  ReservationExtra,
  updateClientApartment,
} from "../../../utils/api/apartmentRentalsAPI";
import {
  calculateReservationPrice,
  isManualQuantityUnit,
  PRICING_UNITS,
  PRICING_UNIT_LABELS,
  PricingUnit,
} from "../../../utils/apartmentCalculator";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
  clientId: string;
  apartments: ClientApartment[];
  onApartmentsChange: (updated: ClientApartment) => void;
  onCreateReservation: (
    apartmentId: string,
    prefill: {
      checkIn?: string;
      checkOut?: string;
      adults?: number;
      children?: number;
      extras?: ReservationExtra[];
    }
  ) => void;
}

const fmtAmount = (n: number, currency?: string) =>
  new Intl.NumberFormat("sr-RS").format(n) + " " + (currency ?? "RSD");

const extraKey = (extra: ApartmentExtra) => (extra._id || extra.name) as string;

const unitLabel = (unit?: string | null) =>
  unit && PRICING_UNIT_LABELS[unit as PricingUnit] ? PRICING_UNIT_LABELS[unit as PricingUnit] : unit || "";

const stepBtnMd: React.CSSProperties = {
  width: 34, height: 34, borderRadius: 8, border: "1px solid #c4bfef",
  background: "#fff", cursor: "pointer", fontWeight: 700, color: "#5b50d6",
  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
  flexShrink: 0,
};
const stepBtnSm: React.CSSProperties = { ...stepBtnMd, width: 26, height: 26, fontSize: 14, borderRadius: 6 };

const Stepper: React.FC<{ value: number; min?: number; onChange: (v: number) => void; size?: "sm" | "md" }> = ({
  value, min = 0, onChange, size = "md",
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: size === "sm" ? 4 : 8 }}>
    <button type="button" style={size === "sm" ? stepBtnSm : stepBtnMd} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
    <span style={{ minWidth: 18, textAlign: "center", fontWeight: 700, fontSize: size === "sm" ? 13 : 15 }}>{value}</span>
    <button type="button" style={size === "sm" ? stepBtnSm : stepBtnMd} onClick={() => onChange(value + 1)}>+</button>
  </div>
);

const CalculatorTab: React.FC<Props> = ({ apartments, onApartmentsChange, onCreateReservation }) => {
  const [checkIn, setCheckIn] = useState<dayjs.Dayjs | null>(null);
  const [checkOut, setCheckOut] = useState<dayjs.Dayjs | null>(null);
  const [adults, setAdults] = useState(2);
  const [childrenUnder2, setChildrenUnder2] = useState(0); // never counted in extras pricing
  const [childrenOver2, setChildrenOver2] = useState(0); // counted normally when the per-extra checkbox is on
  const [apartmentId, setApartmentId] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<Record<string, number>>({}); // extraKey -> quantity
  const [includeChildrenFor, setIncludeChildrenFor] = useState<Record<string, boolean>>({}); // extraKey -> count children too (this calculation only)
  const [discountPercent, setDiscountPercent] = useState(0);
  const [manageOpen, setManageOpen] = useState(false);

  const [addingExtra, setAddingExtra] = useState(false);
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPrice, setNewExtraPrice] = useState(0);
  const [newExtraUnit, setNewExtraUnit] = useState<PricingUnit>("per_unit");
  const [savingExtra, setSavingExtra] = useState(false);

  useEffect(() => {
    if (!apartmentId && apartments.length > 0) setApartmentId(apartments[0]._id);
  }, [apartments, apartmentId]);

  const apartment = apartments.find((a) => a._id === apartmentId) || null;
  const nights = checkIn && checkOut ? Math.max(0, checkOut.diff(checkIn, "day")) : 0;
  const totalChildren = childrenUnder2 + childrenOver2;

  const resetNewExtraForm = () => {
    setAddingExtra(false);
    setNewExtraName("");
    setNewExtraPrice(0);
    setNewExtraUnit("per_unit");
  };

  const handleAddCustomExtra = async () => {
    if (!apartment || !newExtraName.trim() || newExtraPrice <= 0) return;
    setSavingExtra(true);
    try {
      const newExtra: ApartmentExtra = {
        name: newExtraName.trim(),
        regularPrice: newExtraPrice,
        discountedPrice: null,
        unit: newExtraUnit,
      };
      const updatedExtras = [...(apartment.extras || []), newExtra];
      const res = await updateClientApartment(apartment._id, { extras: updatedExtras });
      onApartmentsChange(res.data);
      const saved = res.data.extras[res.data.extras.length - 1];
      setSelected((prev) => ({ ...prev, [extraKey(saved)]: 1 }));
      resetNewExtraForm();
      message.success("Dodatak sačuvan");
    } catch {
      message.error("Greška pri čuvanju dodatka");
    } finally {
      setSavingExtra(false);
    }
  };

  const handleDeleteExtra = async (extra: ApartmentExtra) => {
    if (!apartment) return;
    const key = extraKey(extra);
    try {
      const updatedExtras = (apartment.extras || []).filter((e) => extraKey(e) !== key);
      const res = await updateClientApartment(apartment._id, { extras: updatedExtras });
      onApartmentsChange(res.data);
      setSelected((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      message.success("Stavka obrisana");
    } catch {
      message.error("Greška pri brisanju stavke");
    }
  };

  const toggleExtra = (extra: ApartmentExtra) => {
    const key = extraKey(extra);
    setSelected((prev) => {
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = 1;
      return next;
    });
  };

  const setExtraQty = (extra: ApartmentExtra, qty: number) => {
    const key = extraKey(extra);
    setSelected((prev) => ({ ...prev, [key]: Math.max(1, qty) }));
  };

  const result = useMemo(() => {
    if (!apartment) return null;
    const extrasInput = (apartment.extras || [])
      .filter((e) => extraKey(e) in selected)
      .map((e) => ({
        extraId: extraKey(e),
        name: e.name,
        unit: e.unit,
        unitPrice: e.discountedPrice != null ? e.discountedPrice : e.regularPrice,
        quantity: selected[extraKey(e)] ?? 1,
        includeChildren: includeChildrenFor[extraKey(e)] ?? false,
      }));
    return calculateReservationPrice({
      apartmentName: apartment.name,
      pricePerNight: apartment.pricePerNight,
      nights,
      adults,
      children: childrenOver2,
      extras: extrasInput,
      discountPercent,
    });
  }, [apartment, nights, adults, childrenOver2, selected, includeChildrenFor, discountPercent]);

  const handleCreateReservation = () => {
    if (!apartment || !result || nights <= 0) return;
    const extras: ReservationExtra[] = result.extraItems.map((item) => ({
      extraId: item.key,
      name: item.personsNote ? `${item.label} (${item.personsNote})` : item.label,
      quantity: item.quantity,
      pricePerUnit: item.unitPrice,
      total: item.amount,
    }));
    onCreateReservation(apartment._id, {
      checkIn: checkIn?.format("YYYY-MM-DD"),
      checkOut: checkOut?.format("YYYY-MM-DD"),
      adults,
      children: totalChildren,
      extras,
    });
  };

  return (
    <div className="ar-calc">
      <div className="ar-calc-grid">
        {/* LEFT — reservation inputs */}
        <div className="ar-calc-left">
          <div className="ar-drawer-section-title">Datumi</div>
          <RangePicker
            value={checkIn && checkOut ? [checkIn, checkOut] : null}
            onChange={(dates) => {
              setCheckIn(dates?.[0] ?? null);
              setCheckOut(dates?.[1] ?? null);
            }}
            format="DD.MM.YYYY"
            placeholder={["Check-in", "Check-out"]}
            style={{ width: "100%" }}
            size="large"
          />
          {nights > 0 && (
            <div className="ar-calc-nights">Nights: <strong>{nights}</strong></div>
          )}

          <div className="ar-drawer-section-title" style={{ marginTop: 18 }}>Gosti</div>
          <div className="ar-calc-guests">
            <div className="ar-calc-field">
              <label>Adults</label>
              <Stepper value={adults} min={1} onChange={setAdults} />
            </div>
            <div className="ar-calc-field">
              <label>Djeca do 2 godine</label>
              <Stepper value={childrenUnder2} min={0} onChange={setChildrenUnder2} />
            </div>
            <div className="ar-calc-field">
              <label>Djeca preko 2 godine</label>
              <Stepper value={childrenOver2} min={0} onChange={setChildrenOver2} />
            </div>
          </div>

          <div className="ar-drawer-section-title" style={{ marginTop: 18 }}>Apartman</div>
          <Select
            placeholder="Odaberite apartman"
            value={apartmentId}
            onChange={(v) => { setApartmentId(v); setSelected({}); setIncludeChildrenFor({}); resetNewExtraForm(); }}
            style={{ width: "100%" }}
          >
            {apartments.map((a) => (
              <Option key={a._id} value={a._id}>{a.name}</Option>
            ))}
          </Select>
          {apartment && (
            <div className="ar-calc-apt-price">
              <strong>{fmtAmount(apartment.pricePerNight, apartment.currency)}</strong> / noć
            </div>
          )}

          {apartment && (
            <>
              <div className="ar-drawer-section-title" style={{ marginTop: 18 }}>Dodatne usluge</div>
              {(apartment.extras || []).map((extra) => {
                const key = extraKey(extra);
                const isSel = key in selected;
                const price = extra.discountedPrice != null ? extra.discountedPrice : extra.regularPrice;
                const manual = isManualQuantityUnit(extra.unit);
                const personBased = extra.unit === "per_person" || extra.unit === "per_person_per_night";
                return (
                  <div
                    key={key}
                    className={`ar-calc-extra-row${isSel ? " active" : ""}`}
                    onClick={() => toggleExtra(extra)}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleExtra(extra)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="ar-calc-extra-info">
                      <div className="name">{extra.name}</div>
                      <div className="meta">
                        {fmtAmount(price, apartment.currency)}
                        {extra.unit && <span> · {unitLabel(extra.unit)}</span>}
                      </div>
                      {isSel && personBased && childrenOver2 > 0 && (
                        <label
                          className="ar-calc-children-toggle"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={!!includeChildrenFor[key]}
                            onChange={(e) =>
                              setIncludeChildrenFor((prev) => ({ ...prev, [key]: e.target.checked }))
                            }
                          />
                          Računaj i djecu preko 2 god. ({childrenOver2})
                        </label>
                      )}
                    </div>
                    {isSel && manual && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Stepper size="sm" value={selected[key]} min={1} onChange={(v) => setExtraQty(extra, v)} />
                      </div>
                    )}
                    <div onClick={(e) => e.stopPropagation()}>
                      <Popconfirm
                        title="Izbriši ovu stavku?"
                        onConfirm={() => handleDeleteExtra(extra)}
                        okText="Da, izbriši"
                        cancelText="Otkaži"
                        okButtonProps={{ danger: true }}
                      >
                        <Tooltip title="Izbriši">
                          <button type="button" className="ar-calc-extra-delete">
                            <DeleteOutlined />
                          </button>
                        </Tooltip>
                      </Popconfirm>
                    </div>
                  </div>
                );
              })}

              {!addingExtra ? (
                <button
                  type="button"
                  className="ar-btn ar-btn--ghost"
                  style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
                  onClick={() => setAddingExtra(true)}
                >
                  <PlusOutlined /> Dodaj novu stavku
                </button>
              ) : (
                <div className="ar-calc-new-extra">
                  <Input
                    placeholder="Naziv (npr. Rent-a-car)"
                    value={newExtraName}
                    onChange={(e) => setNewExtraName(e.target.value)}
                    size="small"
                  />
                  <InputNumber
                    placeholder="Cijena"
                    min={0}
                    value={newExtraPrice}
                    onChange={(v) => setNewExtraPrice(Number(v) || 0)}
                    size="small"
                    style={{ width: "100%" }}
                  />
                  <Select
                    value={newExtraUnit}
                    onChange={(v) => setNewExtraUnit(v)}
                    size="small"
                    style={{ width: "100%" }}
                  >
                    {PRICING_UNITS.map((u) => (
                      <Option key={u} value={u}>{PRICING_UNIT_LABELS[u]}</Option>
                    ))}
                  </Select>
                  <div className="ar-calc-new-extra-actions">
                    <Button
                      type="primary"
                      size="small"
                      loading={savingExtra}
                      disabled={!newExtraName.trim() || newExtraPrice <= 0}
                      onClick={handleAddCustomExtra}
                    >
                      Sačuvaj
                    </Button>
                    <Button size="small" onClick={resetNewExtraForm}>Otkaži</Button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="ar-drawer-section-title" style={{ marginTop: 18 }}>Discount</div>
          <InputNumber
            min={0}
            max={100}
            value={discountPercent}
            onChange={(v) => setDiscountPercent(Number(v) || 0)}
            formatter={(v) => `${v}%`}
            parser={(v) => Number((v || "").replace("%", "")) as any}
            style={{ width: 120 }}
          />
        </div>

        {/* RIGHT — price summary */}
        <div className="ar-calc-right">
          <div className="ar-calc-summary-title">Price summary</div>

          {!apartment || nights <= 0 ? (
            <div className="ar-calc-empty">Odaberite apartman i datume za kalkulaciju</div>
          ) : (
            <>
              <div className="ar-calc-lines">
                {result?.apartmentItem && (
                  <div className="ar-calc-line">
                    <div>
                      <div className="label">{result.apartmentItem.label}</div>
                      <div className="detail">
                        {result.apartmentItem.breakdownParts.join(" × ")} × {fmtAmount(result.apartmentItem.unitPrice, apartment.currency)}
                      </div>
                    </div>
                    <div className="amount">{fmtAmount(result.apartmentItem.amount, apartment.currency)}</div>
                  </div>
                )}
                {result?.extraItems.map((item) => (
                  <div className="ar-calc-line" key={item.key}>
                    <div>
                      <div className="label">{item.label}</div>
                      <div className="detail">
                        {item.breakdownParts.length > 0 ? `${item.breakdownParts.join(" × ")} × ` : ""}
                        {fmtAmount(item.unitPrice, apartment.currency)}
                        {item.personsNote && <span> · {item.personsNote}</span>}
                      </div>
                    </div>
                    <div className="amount">{fmtAmount(item.amount, apartment.currency)}</div>
                  </div>
                ))}
              </div>

              <div className="ar-calc-divider" />

              <div className="ar-calc-totals">
                <div className="row">
                  <span>Subtotal</span>
                  <span>{fmtAmount(result!.subtotal, apartment.currency)}</span>
                </div>
                <div className="row">
                  <span>Discount{result!.discountPercent > 0 ? ` (${result!.discountPercent}%)` : ""}</span>
                  <span>-{fmtAmount(result!.discountAmount, apartment.currency)}</span>
                </div>
                <div className="row total">
                  <span>TOTAL</span>
                  <span>{fmtAmount(result!.total, apartment.currency)}</span>
                </div>
              </div>

              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 16, height: 44 }}
                onClick={handleCreateReservation}
              >
                Create Reservation
              </Button>
            </>
          )}

          <Button type="link" onClick={() => setManageOpen(true)} style={{ marginTop: 10, padding: 0 }}>
            Manage Prices
          </Button>
        </div>
      </div>

      <ManagePricesModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        apartments={apartments}
        onApartmentsChange={onApartmentsChange}
      />
    </div>
  );
};

// ─── Manage Prices modal ───────────────────────────────────────────────────────

interface EditState {
  apartmentId: string;
  kind: "apartment" | "extra";
  extraIndex?: number;
  price: number;
  unit?: string;
}

const ManagePricesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  apartments: ClientApartment[];
  onApartmentsChange: (updated: ClientApartment) => void;
}> = ({ open, onClose, apartments, onApartmentsChange }) => {
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setEdit(null);
  }, [open]);

  const startEditApartment = (apt: ClientApartment) =>
    setEdit({ apartmentId: apt._id, kind: "apartment", price: apt.pricePerNight });

  const startEditExtra = (apt: ClientApartment, idx: number) =>
    setEdit({
      apartmentId: apt._id,
      kind: "extra",
      extraIndex: idx,
      price: apt.extras[idx].regularPrice,
      unit: apt.extras[idx].unit || "per_unit",
    });

  const save = async () => {
    if (!edit) return;
    const apt = apartments.find((a) => a._id === edit.apartmentId);
    if (!apt) return;
    setSaving(true);
    try {
      if (edit.kind === "apartment") {
        const res = await updateClientApartment(apt._id, { pricePerNight: edit.price });
        onApartmentsChange(res.data);
      } else if (edit.extraIndex !== undefined) {
        const extras = apt.extras.map((e, i) =>
          i === edit.extraIndex ? { ...e, regularPrice: edit.price, unit: edit.unit } : e
        );
        const res = await updateClientApartment(apt._id, { extras });
        onApartmentsChange(res.data);
      }
      message.success("Cijena ažurirana");
      setEdit(null);
    } catch {
      message.error("Greška pri čuvanju cijene");
    } finally {
      setSaving(false);
    }
  };

  const deleteExtra = async (apt: ClientApartment, idx: number) => {
    setSaving(true);
    try {
      const extras = apt.extras.filter((_, i) => i !== idx);
      const res = await updateClientApartment(apt._id, { extras });
      onApartmentsChange(res.data);
      if (edit?.apartmentId === apt._id && edit.kind === "extra" && edit.extraIndex === idx) {
        setEdit(null);
      }
      message.success("Stavka obrisana");
    } catch {
      message.error("Greška pri brisanju stavke");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Upravljanje cijenama"
      open={open}
      onCancel={onClose}
      footer={null}
      width={580}
      style={{ maxWidth: "94vw", top: 20 }}
    >
      <div className="ar-calc-price-scroll" style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="ar-calc-price-table">
          <div className="ar-calc-price-row ar-calc-price-row--head">
            <span>Stavka</span>
            <span>Cijena</span>
            <span>Jedinica</span>
            <span />
          </div>
          {apartments.map((apt) => (
            <React.Fragment key={apt._id}>
              <div className="ar-calc-price-row">
                <span className="ar-calc-price-item">{apt.name}</span>
                {edit?.apartmentId === apt._id && edit.kind === "apartment" ? (
                  <>
                    <InputNumber
                      size="small"
                      min={0}
                      value={edit.price}
                      onChange={(v) => setEdit({ ...edit, price: Number(v) || 0 })}
                      style={{ width: 90 }}
                    />
                    <span className="ar-calc-price-unit">Noć</span>
                    <span className="ar-calc-price-actions">
                      <button onClick={save} disabled={saving}><CheckOutlined /></button>
                      <button onClick={() => setEdit(null)}><CloseOutlined /></button>
                    </span>
                  </>
                ) : (
                  <>
                    <span>{fmtAmount(apt.pricePerNight, apt.currency)}</span>
                    <span className="ar-calc-price-unit">Noć</span>
                    <span className="ar-calc-price-actions">
                      <button onClick={() => startEditApartment(apt)}><EditOutlined /></button>
                    </span>
                  </>
                )}
              </div>
              {(apt.extras || []).map((extra, idx) => {
                const isEditingThis = edit?.apartmentId === apt._id && edit.kind === "extra" && edit.extraIndex === idx;
                return (
                  <div className="ar-calc-price-row ar-calc-price-row--sub" key={extra._id || `${apt._id}-${idx}`}>
                    <span className="ar-calc-price-item">↳ {extra.name}</span>
                    {isEditingThis ? (
                      <>
                        <InputNumber
                          size="small"
                          min={0}
                          value={edit.price}
                          onChange={(v) => setEdit({ ...edit, price: Number(v) || 0 })}
                          style={{ width: 90 }}
                        />
                        <Select
                          size="small"
                          value={edit.unit}
                          onChange={(v) => setEdit({ ...edit, unit: v })}
                          style={{ width: 140 }}
                        >
                          {PRICING_UNITS.map((u) => (
                            <Option key={u} value={u}>{PRICING_UNIT_LABELS[u]}</Option>
                          ))}
                        </Select>
                        <span className="ar-calc-price-actions">
                          <button onClick={save} disabled={saving}><CheckOutlined /></button>
                          <button onClick={() => setEdit(null)}><CloseOutlined /></button>
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{fmtAmount(extra.discountedPrice ?? extra.regularPrice, apt.currency)}</span>
                        <span className="ar-calc-price-unit">{unitLabel(extra.unit)}</span>
                        <span className="ar-calc-price-actions">
                          <button onClick={() => startEditExtra(apt, idx)}><EditOutlined /></button>
                          <Popconfirm
                            title="Izbriši ovu stavku?"
                            onConfirm={() => deleteExtra(apt, idx)}
                            okText="Da, izbriši"
                            cancelText="Otkaži"
                            okButtonProps={{ danger: true }}
                          >
                            <button><DeleteOutlined /></button>
                          </Popconfirm>
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
          {apartments.length === 0 && (
            <div style={{ padding: 16, textAlign: "center", color: "#9893a8" }}>Nema apartmana</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CalculatorTab;
